// Team service - talks to the backend /teams API (Phase 1 of the Teams
// feature) plus the team-aware fields on /auth/credit-info. Mirrors
// fileWorkspaceService.ts style: plain fetch, typed shapes, no axios.
// Auth header is `${tokenType} ${token}` from localStorage, matching every
// other service in this app.
import config from '../../config';
import type { FileMeta } from './fileWorkspaceService';
import type { UserTeamSummary, BillingSource } from '../../types';

const BASE_URL = config.apiUrl;

export type TeamRole = 'owner' | 'member';

// Which wallet pays for the signed-in user's turns. Defined ONCE in src/types.ts
// (both team values live there, plus the isTeamBilled helper) and re-exported
// here so team code can import the whole contract from one module.
export type { BillingSource };

// The team itself, as returned inside GET /teams/me.
export interface TeamSummary {
  id: number;
  name: string;
  created_at: string; // ISO string (naive UTC from the server)
}

export interface TeamMemberRow {
  user_id: number;
  email: string;
  role: TeamRole;
  monthly_limit: number | null; // credits; null = no cap
  used_this_month: number;      // credits consumed this calendar month (Asia/Jakarta)
}

// Only pending invites are listed, and only for owners.
export interface TeamInviteRow {
  id: number;
  email: string;
  status: string;     // 'pending' in practice; kept loose to match the column
  expires_at: string; // ISO string
}

// The monthly Team plan (pricing v3). Bought by the owner per cycle, exactly
// like the personal subscription: one Xendit invoice, a 31-day clock, and a
// monthly allowance that RESETS on payment (use it or lose it). This is a
// different wallet from the prepaid top-up pool, which carries over.
//
// Every field is optional: a backend that has not shipped the plan yet simply
// omits the block, and the UI then reads as "Belum ada plan Team".
export interface TeamPlan {
  renewed_at?: string | null;   // ISO, when the current cycle was paid
  expires_at?: string | null;   // ISO, when the allowance stops covering turns
  allowance_remaining?: number; // credits left in this cycle
  allowance_total?: number;     // credits the cycle started with
}

// GET /teams/me. The no-team response is `{"team": null}` and carries nothing
// else, so every other field is optional on purpose.
export interface TeamMe {
  team: TeamSummary | null;
  role?: TeamRole;
  user_id?: number;
  balance?: number;             // top-up pool credits (carry over)
  // ISO date the pool stays usable until (12 months from the last top-up).
  // null/absent = nothing bought yet, which is NOT an expiry.
  credits_valid_until?: string | null;
  // null = no plan bought yet or the backend has not shipped the field.
  plan?: TeamPlan | null;
  // Max members the team may hold (base plan + paid extra seats). Absent =
  // unknown, and the UI then stops rendering the "n dari X" quota.
  member_limit?: number;
  // Paid extra seats + their pricing (absent on a backend without seats yet).
  extra_seats?: number;
  seat?: { price_idr: number; credits: number; max_qty: number };
  members?: TeamMemberRow[];
  invites?: TeamInviteRow[];    // owner only, pending only
  my_monthly_used?: number;
  my_monthly_limit?: number | null;
}

// GET /teams/packs - source of truth lives in backend config, never hardcode.
export interface CreditPack {
  key: string;
  name: string;
  price_idr: number;
  credits: number;
  // The live contract sends no badge; kept optional so a future "Paling
  // populer" flag still renders without another round of type edits.
  badge?: string;
}

// The `plan` block of GET /teams/packs: the monthly Team plan on offer, i.e.
// what the owner gets for one invoice. This is the OFFER (catalog), not the
// team's current cycle - that one is TeamPlan on /teams/me.
export interface TeamPlanOffer {
  name: string;        // "Paket Tim"
  price_idr: number;   // 399000
  credits: number;     // monthly allowance the payment resets to
  member_limit: number; // seats included
  days: number;        // cycle length
}

// GET /teams/packs response. `plan` is null when a backend has not shipped the
// block yet, and every caller then falls back to its own copy.
export interface PacksCatalog {
  plan: TeamPlanOffer | null;
  packs: CreditPack[];
}

// 'plan' rows (amount positive) are the monthly allowance being topped back up
// on payment; usage rows spent from that allowance carry a
// " (jatah bulanan)" suffix in their description.
export type LedgerEntryType = 'topup' | 'plan' | 'usage' | 'adjustment';

export interface LedgerEntry {
  id: number;
  entry_type: LedgerEntryType;
  amount: number;        // signed credits: topup positive, usage negative
  balance_after: number;
  user_id: number | null;
  user_email: string | null;
  description: string;
  created_at: string;    // ISO string
}

export interface LedgerPage {
  entries: LedgerEntry[];
  has_more: boolean;
}

export interface TopupResult {
  payment_url: string;
  external_id: string;
}

// POST /teams/plan. The contract only guarantees `payment_url`; the external id
// is echoed back by the current backend but nothing in the UI depends on it, so
// it stays optional.
export interface PlanPurchaseResult {
  payment_url: string;
  external_id?: string;
}

// The team block on /auth/credit-info (null when the user has no team).
// Structurally identical to the shape every component already reads, so it is
// an alias rather than a second declaration that could drift: pricing v3 added
// `allowance_remaining` + `plan_active` there.
export type CreditInfoTeam = UserTeamSummary;

// GET /auth/credit-info. `status` is the EFFECTIVE status (a pool-covered
// member looks active); `raw_status` carries the stored value.
export interface CreditInfo {
  name: string;
  email: string;
  status: string;
  credits_used: number;
  subscription_type?: string;
  credits_total?: number | null; // personal limit; null when unlimited
  billing_source?: BillingSource;
  team?: CreditInfoTeam | null;
  raw_status?: string;
  has_team?: boolean;
}

// Thrown on any non-2xx except 401. `code` is the machine-readable string the
// backend puts in `detail.code` (e.g. 'already_in_team', 'team_payments_closed',
// 'owner_cannot_leave'); it is null when the API answered with a plain string
// detail. `detail` is always safe to render verbatim.
export class TeamError extends Error {
  status: number;
  detail: string;
  code: string | null;
  constructor(status: number, detail: string, code: string | null = null) {
    super(detail);
    this.name = 'TeamError';
    this.status = status;
    this.detail = detail;
    this.code = code;
  }
}

// Thrown on 401. localStorage is already cleared by the time this surfaces, so
// the caller only needs to redirect to /login (existing app-wide pattern).
export class TeamUnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'TeamUnauthorizedError';
  }
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  const tokenType = localStorage.getItem('tokenType');
  return { Authorization: `${tokenType} ${token}` };
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeaders(), 'Content-Type': 'application/json' };
}

// Shared response gate: 401 → clear + TeamUnauthorizedError; other non-2xx →
// pull `detail` (string or {code, message}) out of the JSON body and raise
// TeamError.
async function ensureOk(res: Response): Promise<void> {
  if (res.status === 401) {
    localStorage.clear();
    throw new TeamUnauthorizedError();
  }
  if (!res.ok) {
    let detail = `Permintaan gagal (${res.status})`;
    let code: string | null = null;
    try {
      const data = await res.json();
      const d = data?.detail;
      if (typeof d === 'string' && d) {
        detail = d;
      } else if (d && typeof d === 'object') {
        if (typeof d.code === 'string' && d.code) code = d.code;
        if (typeof d.message === 'string' && d.message) detail = d.message;
        else if (code) detail = code;
      }
    } catch {
      // body wasn't JSON - keep the generic message
    }
    throw new TeamError(res.status, detail, code);
  }
}

// GET /teams/me → { team: null } or the full team payload.
export async function getTeamMe(): Promise<TeamMe> {
  const res = await fetch(`${BASE_URL}/teams/me`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as TeamMe;
}

// POST /teams { name } → /teams/me shape. 409 code 'already_in_team'.
export async function createTeam(name: string): Promise<TeamMe> {
  const res = await fetch(`${BASE_URL}/teams`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name }),
  });
  await ensureOk(res);
  return (await res.json()) as TeamMe;
}

// DELETE /teams → { ok: true }. Owner only, and only while sole member.
export async function deleteTeam(): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

// POST /teams/invites { email } → { invite }. Owner only; sends the Brevo mail.
// 409 codes: 'already_member' | 'already_invited'.
export async function createInvite(email: string): Promise<TeamInviteRow> {
  const res = await fetch(`${BASE_URL}/teams/invites`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ email }),
  });
  await ensureOk(res);
  const data = await res.json();
  return (data?.invite ?? data) as TeamInviteRow;
}

// DELETE /teams/invites/{id} → { ok: true }. Owner only.
export async function revokeInvite(inviteId: number): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams/invites/${inviteId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

// POST /teams/invites/{id}/resend → { invite }. Owner only. Same token, new
// seven-day window, email sent again best-effort.
export async function resendInvite(inviteId: number): Promise<TeamInviteRow> {
  const res = await fetch(`${BASE_URL}/teams/invites/${inviteId}/resend`, {
    method: 'POST',
    headers: jsonHeaders(),
  });
  await ensureOk(res);
  const data = await res.json();
  return (data?.invite ?? data) as TeamInviteRow;
}

// GET /teams/invites/preview?token= → is this invite link still good, and for
// which team? PUBLIC on purpose (the invitee usually has no account yet, and
// the token is the secret), so it deliberately sends NO auth header and never
// goes through ensureOk: any failure, including a backend that has not shipped
// the route, resolves to `{ valid: false }` rather than throwing at the login
// screen. The caller decides what to say about it.
export interface InvitePreview {
  valid: boolean;
  team_name?: string;
  email?: string;
}

export async function previewInvite(token: string): Promise<InvitePreview> {
  if (!token) return { valid: false };
  try {
    const res = await fetch(
      `${BASE_URL}/teams/invites/preview?token=${encodeURIComponent(token)}`
    );
    if (!res.ok) return { valid: false };
    const data = await res.json();
    if (!data?.valid) return { valid: false };
    return {
      valid: true,
      team_name: typeof data.team_name === 'string' ? data.team_name : '',
      email: typeof data.email === 'string' ? data.email : '',
    };
  } catch {
    return { valid: false };
  }
}

// POST /teams/join { token } → /teams/me shape. Idempotent for the same team;
// 400 'invite_invalid', 409 'already_in_team' (a different team).
export async function joinTeam(token: string): Promise<TeamMe> {
  const res = await fetch(`${BASE_URL}/teams/join`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ token }),
  });
  await ensureOk(res);
  return (await res.json()) as TeamMe;
}

// PATCH /teams/members/{user_id} { monthly_limit } → 200. Owner only.
// null clears the cap. The response body is not contractually specified, so
// callers refetch /teams/me instead of trusting a returned row.
export async function updateMemberLimit(
  userId: number,
  monthlyLimit: number | null
): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams/members/${userId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify({ monthly_limit: monthlyLimit }),
  });
  await ensureOk(res);
}

// DELETE /teams/members/{user_id} → 200. Owner only, never self.
export async function removeMember(userId: number): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams/members/${userId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

// POST /teams/leave → 200. Members only; owner gets 403 'owner_cannot_leave'.
export async function leaveTeam(): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams/leave`, {
    method: 'POST',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

// GET /teams/packs → the catalog: the monthly Team plan plus the top-up packs.
// The route is public (no team required), which is what lets the "belum punya
// tim" screen quote the real plan price instead of a hardcoded one.
export async function getPacks(): Promise<PacksCatalog> {
  const res = await fetch(`${BASE_URL}/teams/packs`, { headers: authHeaders() });
  await ensureOk(res);
  const data = await res.json();
  const plan = data?.plan;
  return {
    plan:
      plan && typeof plan === 'object' && Number.isFinite(Number(plan.price_idr))
        ? (plan as TeamPlanOffer)
        : null,
    packs: Array.isArray(data?.packs) ? (data.packs as CreditPack[]) : [],
  };
}

// POST /teams/topup { pack_key } → { payment_url, external_id }. Owner only.
// Raises TeamError with code 'team_payments_closed' (403) while the team money
// flow is frozen.
export async function createTopup(packKey: string): Promise<TopupResult> {
  const res = await fetch(`${BASE_URL}/teams/topup`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ pack_key: packKey }),
  });
  await ensureOk(res);
  return (await res.json()) as TopupResult;
}

// POST /teams/plan → { payment_url }. Owner only, gated by TEAM_PAYMENTS_OPEN
// exactly like the top-up (403 code 'team_payments_closed'). Buys ONE monthly
// cycle of the Team plan; paying resets the team's monthly allowance and
// restarts the 31-day clock, so the same call is both "beli" and "perpanjang".
// success_redirect lands on {frontend_url}/team?plan=success.
export async function purchaseTeamPlan(): Promise<PlanPurchaseResult> {
  const res = await fetch(`${BASE_URL}/teams/plan`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({}),
  });
  await ensureOk(res);
  return (await res.json()) as PlanPurchaseResult;
}

// GET /teams/analytics (owner only): adoption aggregates. AGGREGATES ONLY,
// isi chat tidak pernah lewat sini (kontrak privasi di backend + test).
export type AdoptionState =
  | 'joined'
  | 'trying'
  | 'exploring'
  | 'routine'
  | 'embedded'
  | 'declining';

export interface AdoptionMemberRow {
  user_id: number;
  email: string;
  role: TeamRole;
  joined_at?: string | null;
  activated_at?: string | null;
  adoption_state?: AdoptionState;
  credits_month: number;
  turns_month: number;
  active_days_month: number;
  turns_28d?: number;
  active_days_28d?: number;
  last_active_at: string | null;
  features: { search: number; image: number; file: number; presentation: number };
}

export interface AdoptionInsight {
  id: string;
  headline: string;
  why: string;
  evidence: string;
  primary_action: {
    type: string;
    label: string;
    state?: string;
    invite_ids?: number[];
    uuid?: string;
  };
}

export interface TeamAnalytics {
  month_start: string;
  window_days?: number;
  members: AdoptionMemberRow[];
  invited?: { id: number; email: string; sent_at: string | null; expires_at: string | null }[];
  weekly: { week_start: string; credits: number; turns: number }[];
  insights?: AdoptionInsight[];
  programs?: ProgramSummary[];
  state_counts?: Record<string, number>;
  totals: {
    credits_month: number;
    turns_month: number;
    active_members_month: number;
    member_count: number;
    eligible_members?: number;
    joined_members?: number;
    activated_members?: number;
    activated_7d?: number;
    activation_rate_7d?: number | null;
    wau?: number;
    mau?: number;
    routine_members?: number;
    embedded_members?: number;
    routine_adoption_rate?: number;
    embedded_adoption_rate?: number;
    retained?: number;
    reactivated?: number;
    pending_invites?: number;
    credit_runway_days?: number | null;
  };
}

export async function getTeamAnalytics(): Promise<TeamAnalytics> {
  const res = await fetch(`${BASE_URL}/teams/analytics`, { headers: jsonHeaders() });
  await ensureOk(res);
  return (await res.json()) as TeamAnalytics;
}

export type ExtraRoleKey = 'admin' | 'manager' | 'champion' | 'analytics_viewer';

export interface TeamGroupMember {
  user_id: number;
  email: string;
}

export interface TeamGroupRow {
  id: number;
  name: string;
  description: string | null;
  created_at: string | null;
  member_ids: number[];
  members: TeamGroupMember[];
  manager_ids: number[];
  can_edit_members: boolean;
}

export interface TeamGroupsPayload {
  groups: TeamGroupRow[];
  ungrouped: TeamGroupMember[];
  can_manage_groups: boolean;
}

export interface RoleCatalogItem {
  key: ExtraRoleKey;
  label: string;
  description: string;
}

export interface RoleAssignmentRow {
  id: number;
  user_id: number;
  role_key: ExtraRoleKey;
  scope_group_id: number | null;
  assigned_by: number;
  created_at: string | null;
}

export interface TeamPrivacy {
  heading: string;
  intro: string;
  seen: { what: string; why: string }[];
  never: string[];
}

export interface TeamRolesPayload {
  catalog: RoleCatalogItem[];
  assignments: RoleAssignmentRow[];
  capabilities: string[];
  managed_group_ids: number[];
  can_assign_roles: boolean;
  privacy: TeamPrivacy;
}

export async function getTeamGroups(): Promise<TeamGroupsPayload> {
  const res = await fetch(`${BASE_URL}/teams/groups`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as TeamGroupsPayload;
}

export async function createTeamGroup(name: string, description?: string): Promise<TeamGroupRow> {
  const res = await fetch(`${BASE_URL}/teams/groups`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ name, description: description || undefined }),
  });
  await ensureOk(res);
  return (await res.json()) as TeamGroupRow;
}

export async function archiveTeamGroup(groupId: number): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams/groups/${groupId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

export async function setTeamGroupMembers(groupId: number, userIds: number[]): Promise<TeamGroupRow> {
  const res = await fetch(`${BASE_URL}/teams/groups/${groupId}/members`, {
    method: 'PUT',
    headers: jsonHeaders(),
    body: JSON.stringify({ user_ids: userIds }),
  });
  await ensureOk(res);
  return (await res.json()) as TeamGroupRow;
}

export async function getTeamRoles(): Promise<TeamRolesPayload> {
  const res = await fetch(`${BASE_URL}/teams/roles`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as TeamRolesPayload;
}

export async function assignTeamRole(
  userId: number,
  roleKey: ExtraRoleKey,
  scopeGroupId?: number | null,
): Promise<RoleAssignmentRow> {
  const res = await fetch(`${BASE_URL}/teams/roles/assignments`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      user_id: userId,
      role_key: roleKey,
      scope_group_id: scopeGroupId ?? null,
    }),
  });
  await ensureOk(res);
  return (await res.json()) as RoleAssignmentRow;
}

export async function revokeTeamRole(assignmentId: number): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams/roles/assignments/${assignmentId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

// POST /teams/seats {qty} → Xendit URL. Kursi tambahan Rp75.000/orang/bulan,
// +2.000 kredit per kursi; PAID callback yang menaikkan kuota anggota.
export async function purchaseTeamSeats(qty: number): Promise<PlanPurchaseResult> {
  const res = await fetch(`${BASE_URL}/teams/seats`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ qty }),
  });
  await ensureOk(res);
  return (await res.json()) as PlanPurchaseResult;
}

// GET /teams/ledger?limit&before_id → one page, newest first. Owners see every
// row; members see top-ups plus their own usage.
export async function getLedger(
  limit: number = 50,
  beforeId?: number | null
): Promise<LedgerPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (beforeId != null) params.set('before_id', String(beforeId));
  const res = await fetch(`${BASE_URL}/teams/ledger?${params.toString()}`, {
    headers: authHeaders(),
  });
  await ensureOk(res);
  const data = await res.json();
  return {
    entries: Array.isArray(data?.entries) ? (data.entries as LedgerEntry[]) : [],
    has_more: !!data?.has_more,
  };
}

// GET /auth/credit-info - same endpoint Chat.tsx already polls, typed here so
// the team-aware fields have one definition.
export async function getCreditInfo(): Promise<CreditInfo> {
  const res = await fetch(`${BASE_URL}/auth/credit-info`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as CreditInfo;
}

// ---------------------------------------------------------------------------
// Phase 2 - shared conversations
//
// The visibility + duplicate endpoints live under /conversations, not /teams,
// but they are part of the same feature and reuse authHeaders()/ensureOk()
// here rather than growing a second copy of that logic in conversationService.
// ---------------------------------------------------------------------------

export type ConversationVisibility = 'private' | 'team';

// One row of GET /teams/shared-conversations. Everything the backend does not
// guarantee for older rows is optional so the sidebar degrades gracefully.
export interface SharedConversation {
  id: number;
  uuid: string;
  title: string | null;
  owner_user_id?: number;
  owner_email?: string;
  // The project this conversation is filed in, null when it is shared loose.
  // The sidebar nests rows under their project with it.
  project_id?: number | null;
  updated_at?: string;
  // Annotated the same way list_conversations does it.
  active_run_status?: 'running' | 'just_done' | null;
}

// PATCH /conversations/uuid/{uuid}/visibility response.
export interface VisibilityResult {
  uuid: string;
  visibility: ConversationVisibility;
  shared_team_id: number | null;
}

// POST /conversations/uuid/{uuid}/duplicate response.
export interface DuplicateResult {
  id: number;
  uuid: string;
  title: string | null;
}

// GET /teams/shared-conversations → conversations shared with my team
// (including my own shared ones), newest first. Members only: a user without a
// team gets 404 'no_team', which the caller is expected to swallow silently
// (that is also what a backend without this endpoint yet returns).
export async function getSharedConversations(): Promise<SharedConversation[]> {
  const res = await fetch(`${BASE_URL}/teams/shared-conversations`, {
    headers: authHeaders(),
  });
  await ensureOk(res);
  const data = await res.json();
  return Array.isArray(data?.conversations)
    ? (data.conversations as SharedConversation[])
    : [];
}

// PATCH /conversations/uuid/{uuid}/visibility { visibility } → the new state.
// Owner only (404 when not found / not owner); 'team' needs a team
// (400 code 'no_team').
export async function setConversationVisibility(
  uuid: string,
  visibility: ConversationVisibility
): Promise<VisibilityResult> {
  const res = await fetch(
    `${BASE_URL}/conversations/uuid/${encodeURIComponent(uuid)}/visibility`,
    {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify({ visibility }),
    }
  );
  await ensureOk(res);
  return (await res.json()) as VisibilityResult;
}

// POST /conversations/uuid/{uuid}/duplicate → a private copy owned by me
// (title + " (salinan)"). Requires read access only, so a team member can
// duplicate someone else's shared conversation and continue it themselves.
export async function duplicateConversation(uuid: string): Promise<DuplicateResult> {
  const res = await fetch(
    `${BASE_URL}/conversations/uuid/${encodeURIComponent(uuid)}/duplicate`,
    {
      method: 'POST',
      headers: authHeaders(),
    }
  );
  await ensureOk(res);
  return (await res.json()) as DuplicateResult;
}

// ---------------------------------------------------------------------------
// Phase 3 - projects
//
// A project groups conversations + files under shared `instructions` that AUTO
// injects into every chat inside it. Visibility mirrors the conversation model:
// 'team' = every member sees it, 'private' = creator only.
//
// EVERY field the backend does not guarantee is optional here on purpose: this
// contract is being built in parallel, and a 404 (route not shipped yet) must
// degrade to "no projects" rather than break the sidebar or a page.
// ---------------------------------------------------------------------------

export type ProjectVisibility = 'team' | 'private';

// One row of GET /teams/projects. `instructions` only comes back on the detail
// endpoint, so it is optional on the list shape too.
export interface ProjectRow {
  id: number;
  name: string;
  description?: string | null;
  instructions?: string | null;
  visibility?: ProjectVisibility;
  created_by?: number;
  created_by_email?: string;
  conversation_count?: number;
  created_at?: string;
}

// One file row inside a project. Structurally the workspace-file shape from
// fileWorkspaceService (FileMeta); re-declared as a type alias so callers can
// hand these straight to prettySize()/fileFamily() without a cast.
export type ProjectFile = FileMeta;

// GET /teams/projects/{id}. Conversations reuse the shared-conversation row
// shape (title / owner / updated_at / active_run_status).
export interface ProjectDetail {
  project: ProjectRow;
  conversations: SharedConversation[];
  files: ProjectFile[];
}

// Body of POST /teams/projects and PATCH /teams/projects/{id}. Every field is
// optional on PATCH; only `name` is required on create (enforced by the caller).
export interface ProjectInput {
  name?: string;
  description?: string | null;
  instructions?: string | null;
  visibility?: ProjectVisibility;
}

// PATCH /conversations/uuid/{uuid}/project response. `visibility` is what makes
// the auto-share side effect visible to the client: moving a conversation into
// a TEAM project flips it to 'team' server-side.
export interface ConversationProjectResult {
  uuid: string;
  project_id: number | null;
  visibility: ConversationVisibility;
}

// GET /teams/projects → team projects plus the caller's own private ones.
// Members only; a user without a team (or a backend without the route) answers
// 404, which the caller is expected to swallow into an empty list.
export async function listProjects(): Promise<ProjectRow[]> {
  const res = await fetch(`${BASE_URL}/teams/projects`, { headers: authHeaders() });
  await ensureOk(res);
  const data = await res.json();
  return Array.isArray(data?.projects) ? (data.projects as ProjectRow[]) : [];
}

// POST /teams/projects { name, description?, visibility? } → the new project.
export async function createProject(input: ProjectInput): Promise<ProjectRow> {
  const res = await fetch(`${BASE_URL}/teams/projects`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(input),
  });
  await ensureOk(res);
  const data = await res.json();
  return (data?.project ?? data) as ProjectRow;
}

// PATCH /teams/projects/{id} → the updated project. Creator or team owner only
// (403 code 'not_project_editor' for anyone else). Only the keys present in
// `patch` are changed.
export async function updateProject(
  projectId: number,
  patch: ProjectInput
): Promise<ProjectRow> {
  const res = await fetch(`${BASE_URL}/teams/projects/${projectId}`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    body: JSON.stringify(patch),
  });
  await ensureOk(res);
  const data = await res.json();
  return (data?.project ?? data) as ProjectRow;
}

// DELETE /teams/projects/{id} → { ok: true }. Creator or team owner. The
// conversations inside it survive (their project_id is cleared); project files
// are removed from storage.
export async function deleteProject(projectId: number): Promise<void> {
  const res = await fetch(`${BASE_URL}/teams/projects/${projectId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  await ensureOk(res);
}

// GET /teams/projects/{id} → project (incl. instructions) + conversations +
// files. 404/403 mean "not visible to me" and the page bounces to /team.
export async function getProjectDetail(projectId: number): Promise<ProjectDetail> {
  const res = await fetch(`${BASE_URL}/teams/projects/${projectId}`, {
    headers: authHeaders(),
  });
  await ensureOk(res);
  const data = await res.json();
  return {
    project: (data?.project ?? {}) as ProjectRow,
    conversations: Array.isArray(data?.conversations)
      ? (data.conversations as SharedConversation[])
      : [],
    files: Array.isArray(data?.files) ? (data.files as ProjectFile[]) : [],
  };
}

// PATCH /conversations/uuid/{uuid}/project { project_id } → the new state.
// Conversation owner only, and the caller must be able to see the project.
// Passing null removes the conversation from its project WITHOUT unsharing it.
// Moving into a TEAM project auto-shares the conversation, which is why the
// response echoes `visibility` - callers must adopt it, not assume.
export async function setConversationProject(
  uuid: string,
  projectId: number | null
): Promise<ConversationProjectResult> {
  const res = await fetch(
    `${BASE_URL}/conversations/uuid/${encodeURIComponent(uuid)}/project`,
    {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify({ project_id: projectId }),
    }
  );
  await ensureOk(res);
  return (await res.json()) as ConversationProjectResult;
}

export interface PlaybookInputField {
  key: string;
  label: string;
  required?: boolean;
}

export interface PlaybookVersion {
  id: number;
  version_number: number;
  input_schema: PlaybookInputField[];
  output_format: string | null;
  example_output: string | null;
  estimated_minutes_without_ai: number | null;
  estimated_minutes_with_ai: number | null;
  published_at: string | null;
  instructions?: string;
}

export interface PlaybookRow {
  id: number;
  uuid: string;
  name: string;
  description: string | null;
  outcome_category: string | null;
  status: 'draft' | 'published' | 'archived' | string;
  visibility: string;
  owner_user_id: number;
  linked_project_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  can_edit: boolean;
  version: PlaybookVersion | null;
  published_version: PlaybookVersion | null;
  assignments: { id: number; group_id: number | null; user_id: number | null }[];
}

export interface PlaybookListPayload {
  playbooks: PlaybookRow[];
  can_draft: boolean;
  can_publish: boolean;
}

export interface PlaybookStartResult {
  run_id: number;
  conversation_id: number;
  conversation_uuid: string;
  playbook_uuid: string;
  playbook_name: string;
  version_number: number;
  status: string;
}

export interface PlaybookRunSummary {
  id: number;
  playbook_uuid: string | null;
  playbook_name: string | null;
  version_number: number | null;
  status: string;
}

export interface PlaybookAnalytics {
  playbook_uuid: string;
  name: string;
  starts: number;
  completions: number;
  useful_responses: number;
  useful_yes: number;
  minutes_saved_sum: number;
  credits: number;
}

export interface PlaybookDraftBody {
  name: string;
  description?: string;
  outcome_category?: string;
  instructions?: string;
  input_schema?: PlaybookInputField[];
  output_format?: string;
  example_output?: string;
  estimated_minutes_without_ai?: number | null;
  estimated_minutes_with_ai?: number | null;
}

export async function listPlaybooks(): Promise<PlaybookListPayload> {
  const res = await fetch(`${BASE_URL}/teams/playbooks`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as PlaybookListPayload;
}

export async function getPlaybook(uuid: string): Promise<PlaybookRow> {
  const res = await fetch(
    `${BASE_URL}/teams/playbooks/${encodeURIComponent(uuid)}`,
    { headers: authHeaders() },
  );
  await ensureOk(res);
  return (await res.json()) as PlaybookRow;
}

export async function createPlaybook(body: PlaybookDraftBody): Promise<PlaybookRow> {
  const res = await fetch(`${BASE_URL}/teams/playbooks`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  await ensureOk(res);
  return (await res.json()) as PlaybookRow;
}

export async function updatePlaybook(
  uuid: string,
  body: Partial<PlaybookDraftBody>,
): Promise<PlaybookRow> {
  const res = await fetch(
    `${BASE_URL}/teams/playbooks/${encodeURIComponent(uuid)}`,
    {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify(body),
    },
  );
  await ensureOk(res);
  return (await res.json()) as PlaybookRow;
}

export async function publishPlaybook(uuid: string): Promise<PlaybookRow> {
  const res = await fetch(
    `${BASE_URL}/teams/playbooks/${encodeURIComponent(uuid)}/publish`,
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  await ensureOk(res);
  return (await res.json()) as PlaybookRow;
}

export async function startPlaybook(
  uuid: string,
  inputs?: Record<string, string>,
): Promise<PlaybookStartResult> {
  const res = await fetch(
    `${BASE_URL}/teams/playbooks/${encodeURIComponent(uuid)}/start`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ inputs: inputs || {} }),
    },
  );
  await ensureOk(res);
  return (await res.json()) as PlaybookStartResult;
}

export async function completePlaybookRun(
  runId: number,
  body?: { useful?: boolean; minutes_saved?: number },
): Promise<{ id: number; status: string; useful: number | null; minutes_saved: number | null }> {
  const res = await fetch(
    `${BASE_URL}/teams/playbook-runs/${runId}/complete`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify(body || {}),
    },
  );
  await ensureOk(res);
  return await res.json();
}

export async function getPlaybookAnalytics(uuid: string): Promise<PlaybookAnalytics> {
  const res = await fetch(
    `${BASE_URL}/teams/playbooks/${encodeURIComponent(uuid)}/analytics`,
    { headers: authHeaders() },
  );
  await ensureOk(res);
  return (await res.json()) as PlaybookAnalytics;
}

export interface ProgramSummary {
  uuid: string;
  name: string;
  status: string;
  participants: number;
  completed: number;
}

export interface ProgramMission {
  id: number;
  position: number;
  name: string;
  description: string | null;
  mission_type: 'playbook' | 'custom' | string;
  playbook_id: number | null;
  playbook_name?: string | null;
  my_status: string | null;
}

export interface ProgramRow {
  id: number;
  uuid: string;
  name: string;
  objective: string | null;
  status: string;
  audience_group_id: number | null;
  owner_user_id: number;
  created_at: string | null;
  launched_at: string | null;
  participant_count: number;
  completed_count: number;
  missions: ProgramMission[];
  my_status: string | null;
  muted: boolean;
  progress?: { not_started: number; in_progress: number; completed: number };
  notifications_sent?: number;
}

export interface ProgramListPayload {
  programs: ProgramRow[];
  can_create: boolean;
}

export interface ProgramInboxItem {
  id: number;
  template_key: string;
  status: string;
  sent_at: string | null;
  program_uuid: string | null;
  program_name: string | null;
}

export async function listPrograms(): Promise<ProgramListPayload> {
  const res = await fetch(`${BASE_URL}/teams/programs`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as ProgramListPayload;
}

export async function getMyPrograms(): Promise<{ programs: ProgramRow[] }> {
  const res = await fetch(`${BASE_URL}/teams/programs/mine`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as { programs: ProgramRow[] };
}

export async function getProgram(uuid: string): Promise<ProgramRow> {
  const res = await fetch(
    `${BASE_URL}/teams/programs/${encodeURIComponent(uuid)}`,
    { headers: authHeaders() },
  );
  await ensureOk(res);
  return (await res.json()) as ProgramRow;
}

export async function createProgram(body: {
  name: string;
  objective?: string;
  group_id?: number | null;
  missions: { name: string; description?: string; mission_type?: string; playbook_uuid?: string }[];
}): Promise<ProgramRow> {
  const res = await fetch(`${BASE_URL}/teams/programs`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });
  await ensureOk(res);
  return (await res.json()) as ProgramRow;
}

export async function launchProgram(uuid: string): Promise<ProgramRow> {
  const res = await fetch(
    `${BASE_URL}/teams/programs/${encodeURIComponent(uuid)}/launch`,
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  await ensureOk(res);
  return (await res.json()) as ProgramRow;
}

export async function completeProgramMission(uuid: string, missionId: number): Promise<ProgramRow> {
  const res = await fetch(
    `${BASE_URL}/teams/programs/${encodeURIComponent(uuid)}/missions/${missionId}/complete`,
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  await ensureOk(res);
  return (await res.json()) as ProgramRow;
}

export async function muteProgram(uuid: string): Promise<void> {
  const res = await fetch(
    `${BASE_URL}/teams/programs/${encodeURIComponent(uuid)}/mute`,
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  await ensureOk(res);
}

export async function remindProgram(uuid: string): Promise<{ sent: number; skipped: number }> {
  const res = await fetch(
    `${BASE_URL}/teams/programs/${encodeURIComponent(uuid)}/remind`,
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  await ensureOk(res);
  return await res.json();
}

export async function getProgramProgress(uuid: string): Promise<{
  uuid: string;
  name: string;
  status: string;
  not_started: number;
  in_progress: number;
  completed: number;
  members: { user_id: number; email: string | null; status: string; completed_at: string | null }[];
}> {
  const res = await fetch(
    `${BASE_URL}/teams/programs/${encodeURIComponent(uuid)}/progress`,
    { headers: authHeaders() },
  );
  await ensureOk(res);
  return await res.json();
}

export async function listNotifications(): Promise<{ notifications: ProgramInboxItem[]; unread: number }> {
  const res = await fetch(`${BASE_URL}/teams/notifications`, { headers: authHeaders() });
  await ensureOk(res);
  return await res.json();
}

export type HelpTopic = 'misi' | 'playbook' | 'kredit' | 'lainnya';

export interface HelpRequestRow {
  id: number;
  user_id: number;
  user_email: string | null;
  topic: HelpTopic | string;
  note: string | null;
  status: 'open' | 'answered' | 'closed' | string;
  reply_text: string | null;
  answered_by: number | null;
  answered_email: string | null;
  program_uuid: string | null;
  program_name: string | null;
  mission_id: number | null;
  mission_name: string | null;
  created_at: string | null;
  answered_at: string | null;
}

export interface HelpQueuePayload {
  open_count: number;
  champion_count: number;
  requests: HelpRequestRow[];
}

export interface AdoptionDigest {
  week_key: string;
  wau: number;
  mau: number;
  routine_adoption_rate: number;
  joined_unactivated: number;
  declining: number;
  open_help: number;
  champion_count: number;
  pending_invites: number;
  insights: { id: string; headline: string; why: string; evidence: string; primary_action?: { type: string; label: string } }[];
  can_send?: boolean;
  sent?: number;
}

export async function listMyHelp(): Promise<{ requests: HelpRequestRow[] }> {
  const res = await fetch(`${BASE_URL}/teams/help`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as { requests: HelpRequestRow[] };
}

export async function requestProgramHelp(
  programUuid: string,
  body: { topic?: HelpTopic; note?: string; mission_id?: number },
): Promise<HelpRequestRow> {
  const res = await fetch(
    `${BASE_URL}/teams/programs/${encodeURIComponent(programUuid)}/help`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) },
  );
  await ensureOk(res);
  return (await res.json()) as HelpRequestRow;
}

export async function getHelpQueue(status?: string): Promise<HelpQueuePayload> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`${BASE_URL}/teams/help-queue${qs}`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as HelpQueuePayload;
}

export async function replyHelpRequest(id: number, replyText: string): Promise<HelpRequestRow> {
  const res = await fetch(`${BASE_URL}/teams/help-queue/${id}/reply`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ reply_text: replyText }),
  });
  await ensureOk(res);
  return (await res.json()) as HelpRequestRow;
}

export async function closeHelpRequest(id: number): Promise<HelpRequestRow> {
  const res = await fetch(`${BASE_URL}/teams/help-queue/${id}/close`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: '{}',
  });
  await ensureOk(res);
  return (await res.json()) as HelpRequestRow;
}

export async function getAdoptionDigest(): Promise<AdoptionDigest> {
  const res = await fetch(`${BASE_URL}/teams/adoption/digest`, { headers: authHeaders() });
  await ensureOk(res);
  return (await res.json()) as AdoptionDigest;
}

export async function sendAdoptionDigest(): Promise<AdoptionDigest> {
  const res = await fetch(`${BASE_URL}/teams/adoption/digest/send`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: '{}',
  });
  await ensureOk(res);
  return (await res.json()) as AdoptionDigest;
}

export async function readNotification(id: number): Promise<void> {
  const res = await fetch(
    `${BASE_URL}/teams/notifications/${id}/read`,
    { method: 'POST', headers: jsonHeaders(), body: '{}' },
  );
  await ensureOk(res);
}

// ---------------------------------------------------------------------------
// Formatting helpers shared by the team UI
// ---------------------------------------------------------------------------

// Credits are floats server-side but read as counters, so trim to at most one
// decimal and never show a trailing ",0".
export function formatCredits(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return value.toLocaleString('id-ID', { maximumFractionDigits: 1 });
}

// Rupiah, no decimals: 199000 → "Rp199.000"
export function formatRupiah(value: number): string {
  if (!Number.isFinite(value)) return 'Rp0';
  return `Rp${Math.round(value).toLocaleString('id-ID')}`;
}
